import { deskMay, fillBar, sessionFill, type BusinessSession, type MyBusiness } from "@teamna/shared";
import { useState } from "react";
import { useTimetableEditor } from "@teamna/web-client/hooks/use-timetable.ts";
import { formatDayHeading, formatDayLong, instantFromZonedTime } from "@teamna/web-client/lib/format.ts";
import { Avatar, Button, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, EmptyState, ErrorState, SkeletonList, StepArrows } from "../../components/index.ts";
import { useOwnerSessions } from "@teamna/web-client/hooks/use-owner.ts";
import { sessionColor } from "../../lib/group-color.ts";
import { CalendarBoard, type CalendarBlock, type CalendarColumn } from "../calendar/CalendarBoard.tsx";
import { shortTimeLabel, timeLabel } from "../calendar/layout.ts";
import { localMinutes, useScheduleWords } from "../schedule-grid.tsx";
import { NO_ONE } from "../timetable/CoachStrip.tsx";
import { todayProgress, todayStatusText, type TodayProgress } from "./progress.ts";
import { SessionPanelContent } from "./SessionPanel.tsx";
import { TodayChips } from "./TodayChips.tsx";
import { TodayStale } from "./TodayStale.tsx";
import { useTodaySessions } from "./use-today.ts";

/**
 * The Day view of the Calendar page at 1024px and up (named Today until 2026-10-21): the sessions of
 * one day on the same calendar as the Week and Schedule views (`calendar/CalendarBoard.tsx`), one
 * column per coach. It opens on today, with a line at the time now. The arrows move one day back or
 * forward, and "Back to today" returns. The panel on the right collapses to a thin strip (the round
 * button on its edge, or the close button of a session), so the calendar gets the full width; a
 * click on a session opens it again. A click on a session opens it in the panel on the right: the same rail as the Week
 * view (`SessionRail`: who is coming, the coach, move, cancel), with Take attendance kept at the
 * bottom of the panel. Take attendance swaps in the same `TakeAttendanceScreen` the coaches use;
 * its back link returns to the session. Nothing about either is different here.
 *
 * The panel opens on the session that needs attendance first: one running now, else one that
 * ended with no marks, else the next one.
 */

const DEFAULT_DAY = { start: 8 * 60, end: 20 * 60 };

/** The session the panel opens on before the owner picks one. */
function firstToTake(rows: Array<{ session: BusinessSession; progress: TodayProgress }>): string | null {
  const order: TodayProgress["state"][] = ["now", "partial", "missed", "later"];
  for (const state of order) {
    const row = rows.find((entry) => entry.progress.state === state);
    if (row) return row.session.id;
  }
  return null;
}

export function TodayView({ business }: { business: MyBusiness }) {
  const businessId = business.businessId;
  const timezone = business.timezone || "Asia/Dubai";
  // The group colours and the team. The front desk (T1) does not read the editor: the default colours.
  const editor = useTimetableEditor(deskMay(business.staffRole, "readTimetableEditor", business.frontDesk) ? businessId : null);
  // 0 is today, -1 yesterday, 1 tomorrow.
  const [dayOffset, setDayOffset] = useState(0);
  const { query, sessions, shownKey, now, range: day } = useTodaySessions(businessId, timezone, dayOffset);
  // The same sessions as the Week view reads them, for the session rail.
  const owned = useOwnerSessions({ businessId, from: day.from, to: day.to });
  const words = useScheduleWords();
  const Staff = `${words.staff.charAt(0).toUpperCase()}${words.staff.slice(1)}`;
  const [picked, setPicked] = useState<string | null>(null);
  // The panel shows the session (the Week view's rail); Take attendance swaps in the attendance screen.
  // The session whose attendance is open: tied to its id, so a list change never opens another one.
  const [attendingId, setAttendingId] = useState<string | null>(null);
  // Closed by the owner: the panel stays empty until they pick a session again.
  const [closed, setClosed] = useState(false);
  // The panel is a thin strip and the calendar takes the width. It stays so from day to day.
  const [panelHidden, setPanelHidden] = useState(false);

  /** Show another day: the panel starts again on that day's first session to take. */
  function showDay(offset: number) {
    setDayOffset(offset);
    setPicked(null);
    setClosed(false);
    setAttendingId(null);
  }

  const isToday = dayOffset === 0;
  const shownIso = instantFromZonedTime(shownKey, 12, 0, timezone);
  const dayTitle = formatDayLong(shownIso, timezone);
  const loading = (query.isLoading && !query.data) || (editor.isLoading && !editor.data);
  const rows = loading ? [] : sessions.map((session) => ({ session, progress: todayProgress(session, now) }));
  const nowMinute = isToday ? localMinutes(new Date(now).toISOString(), timezone) : undefined;

  // The day and the arrows stay on screen while a day loads, fails or is empty.
  const heading = (
    <div className="flex flex-wrap items-center gap-3">
      <StepArrows unit="day" onPrevious={() => showDay(dayOffset - 1)} onNext={() => showDay(dayOffset + 1)} />
      <h2 className="text-[17px] font-semibold">{formatDayHeading(shownIso, timezone)}</h2>
      {isToday ? null : (
        <Button variant="ghost" size="sm" onClick={() => showDay(0)}>
          Back to today
        </Button>
      )}
      <TodayChips progress={rows.map((row) => row.progress)} />
      {query.isError && query.data ? <TodayStale onRetry={() => void query.refetch()} /> : null}
    </div>
  );

  if (loading) {
    return (
      <div className="grid gap-3">
        {heading}
        <SkeletonList rows={4} />
      </div>
    );
  }
  if (query.error && !query.data) {
    return (
      <div className="grid gap-3">
        {heading}
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  const levels = editor.data?.groups ?? [];
  const team = editor.data?.team ?? [];

  if (rows.length === 0) {
    return (
      <div className="grid gap-3">
        {heading}
        <div className="rounded-card border border-line bg-paper p-6 shadow-card">
          <EmptyState
            icon={<CalendarIcon size={30} />}
            title={isToday ? "No sessions today" : "No sessions on this day"}
            message={`${dayTitle} is clear. Use the arrows to see another day.`}
          />
        </div>
      </div>
    );
  }

  // The first session to take opens only before the owner picks one. A picked session that left the
  // day (moved) closes the panel: it never opens another session in its place.
  const openId = closed ? null : picked ? (rows.some((row) => row.session.id === picked) ? picked : null) : firstToTake(rows);

  // One column per coach who runs a session today, in the order of their first session.
  const coachIds: string[] = [];
  for (const { session } of rows) {
    const id = session.staffUserId ?? NO_ONE;
    if (!coachIds.includes(id)) coachIds.push(id);
  }
  const nameOf = (id: string) =>
    id === NO_ONE ? `No ${words.staff}` : (team.find((member) => member.userId === id)?.name ?? rows.find((row) => row.session.staffUserId === id)?.session.staffName ?? Staff);
  const columns: CalendarColumn[] = coachIds.map((id) => {
    const count = rows.filter((row) => (row.session.staffUserId ?? NO_ONE) === id).length;
    const name = nameOf(id);
    const subtitle = `${count} ${count === 1 ? "session" : "sessions"}`;
    const photoUrl = team.find((member) => member.userId === id)?.photoUrl ?? null;
    const avatar = id === NO_ONE ? undefined : <Avatar name={name} photoUrl={photoUrl} size={24} />;
    return { key: id, title: name, subtitle, label: `${name}, ${subtitle}`, off: false, avatar };
  });

  const blocks: CalendarBlock[] = rows.map(({ session, progress }, index) => {
    const start = localMinutes(session.startsAt, timezone);
    const end = Math.min(24 * 60, start + session.durationMin);
    const status = todayStatusText(progress, session, timezone);
    // Booked of the places, as a bar along the lower edge (D38): one colour, gold when people wait.
    const bar = progress.state === "cancelled" ? null : fillBar(sessionFill(session));
    return {
      key: session.id,
      id: session.id,
      column: session.staffUserId ?? NO_ONE,
      start,
      end,
      color: sessionColor(levels, session.groupId, session.groupColor, index),
      meter: bar,
      label: `${session.groupName} at ${timeLabel(start)}, ${nameOf(session.staffUserId ?? NO_ONE)}, ${status}`,
      time: shortTimeLabel(start),
      title: session.groupName,
      detail: status,
      struck: progress.state === "cancelled",
      selected: session.id === openId,
      flagged: progress.state === "missed",
    };
  });

  const range = { ...DEFAULT_DAY };
  for (const block of blocks) {
    range.start = Math.min(range.start, Math.floor(block.start / 60) * 60);
    range.end = Math.max(range.end, Math.ceil(block.end / 60) * 60);
  }

  const open = openId ? rows.find((row) => row.session.id === openId) : undefined;

  return (
    <div className="grid gap-3">
      {heading}

      <div
        className={[
          // The panel's width slides when it collapses or opens.
          "relative grid h-[calc(100vh-252px)] min-h-[560px] grid-rows-[minmax(0,1fr)] overflow-clip rounded-card border border-line bg-paper shadow-card transition-[grid-template-columns] duration-200 ease-out motion-reduce:transition-none",
          panelHidden ? "grid-cols-[minmax(0,1fr)_48px]" : "grid-cols-[minmax(0,1fr)_380px]",
        ].join(" ")}
      >
        <section aria-label={isToday ? "Sessions today" : `Sessions on ${dayTitle}`} className="relative min-h-0 min-w-0 overflow-auto bg-surface">
          <CalendarBoard
            key={shownKey}
            columns={columns}
            blocks={blocks}
            usualHours={range}
            nowMinute={nowMinute}
            showNow={isToday}
            hoverPreview
            minWidth={52 + columns.length * 80}
            onSelect={(id) => {
              setPicked(id);
              setClosed(false);
              setAttendingId(null);
              setPanelHidden(false);
            }}
          />
        </section>
        {panelHidden ? null : (
          // On the edge between the calendar and the panel, so it never covers the panel's own buttons.
          <button
            type="button"
            onClick={() => setPanelHidden(true)}
            aria-label="Hide the session panel"
            title="Hide the session panel"
            className="absolute top-3 right-[380px] z-20 flex size-7 translate-x-1/2 items-center justify-center rounded-full border border-line bg-paper text-ink-3 shadow-card transition-colors duration-150 hover:text-ink"
          >
            <ChevronRightIcon size={15} />
          </button>
        )}
        <aside aria-label="Selected session" className="flex min-h-0 min-w-0 flex-col overflow-x-hidden overflow-y-auto border-l border-line">
          {panelHidden ? (
            <button
              type="button"
              onClick={() => setPanelHidden(false)}
              aria-label="Show the session panel"
              title="Show the session panel"
              className="flex h-full w-full flex-col items-center gap-2 pt-3 text-ink-3 transition-colors duration-150 hover:bg-surface hover:text-ink"
            >
              <ChevronLeftIcon size={17} />
              <span className="text-[12.5px] font-semibold [writing-mode:vertical-rl]">{open ? open.session.groupName : "Session"}</span>
            </button>
          ) : open ? (
            <SessionPanelContent
              business={business}
              open={open}
              owned={owned}
              editor={editor.data}
              attending={attendingId === open.session.id}
              onAttendingChange={(attending) => setAttendingId(attending ? open.session.id : null)}
              attendanceSource={isToday ? "calendar_today" : "calendar_day"}
              onClose={() => {
                setClosed(true);
                setPanelHidden(true);
              }}
            />
          ) : (
            <div className="p-5">
              <EmptyState
                icon={<CalendarIcon size={28} />}
                title="Pick a session"
                message="Click a session on the calendar to open it here. Take attendance is at the bottom of the panel."
              />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
